import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import AdminSideBar from "./Admin-SideBar";
import Youroheader from "../Youro-header";
import { errorMessage } from "../../utils/errorMessage";
import {
  DEFAULT_RANGE,
  EMPTY,
  RANGES,
  count,
  decimal,
  fetchAccess,
  fetchMembership,
  fetchOutcomes,
  fetchOverview,
  money,
  percent,
  rangeCaption,
  sum,
  token,
} from "../../utils/adminAnalyticsApi";
import "../../styles/Admin-ui/Admin-Analytics.css";

/**
 * The admin analytics console.
 *
 * <p>Eight tabs, because the eight questions the business actually asks do not
 * fit on one screen. Four of them have data today; the other four say plainly
 * what is missing and when it arrives, rather than showing an empty chart that
 * looks like a finding. A tab that reads "nothing records this yet" is more
 * useful than one reading "0".
 *
 * <p>Two rules run through the whole screen:
 *
 *  - A dash means "nothing to measure". A zero means zero. They are different
 *    facts and the screen never turns the first into the second — see the
 *    formatters in utils/adminAnalyticsApi.
 *  - Nothing here receives a patient. Every endpoint behind it returns counts and
 *    averages, so there is no path by which a name could appear on a chart.
 */

/** Which tabs exist, in the order the questions get asked. */
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "funnel", label: "Funnel" },
  { key: "acquisition", label: "Acquisition" },
  { key: "membership", label: "Membership & revenue" },
  { key: "access", label: "Access" },
  { key: "outcomes", label: "Outcomes" },
  { key: "satisfaction", label: "Satisfaction" },
  { key: "pivot", label: "Pivot signals" },
];

/** Tabs whose data does not exist yet, and what has to happen for it to. */
const NOT_YET = {
  funnel: {
    heading: "The funnel needs visit tracking",
    body: [
      "Assessments completed, accounts created and payments taken are all recorded, so three steps of the funnel are already countable. The two missing steps are the ones that happen before anybody signs in: a landing-page visit and an assessment that was started and abandoned. Nothing records either.",
      "That is first-party event collection — Youro's own server, no third-party pixel — and it is the next slice of this work.",
    ],
  },
  acquisition: {
    heading: "Nothing records how people found Youro",
    body: [
      "There is no \"how did you hear about us\" field and no campaign tracking. The one source column that exists is a fixed internal string, not a channel.",
      "An optional question at signup is built and waiting on legal review before it ships, since it touches what is collected from a patient at the point of care.",
    ],
  },
  satisfaction: {
    heading: "No ratings are collected yet",
    body: [
      "Visit ratings, a periodic satisfaction score and a reason-for-leaving question are all additive and all optional for the patient. None of them exists today, so there is nothing to average.",
      "The cancellation question in particular has to appear after a cancellation completes, never as a step inside it — nobody should have to answer a survey to stop paying.",
    ],
  },
  pivot: {
    heading: "Partly here, partly not",
    body: [
      "Condition mix, how many members have more than one diagnosis, age band and sex are all derivable from what is already stored, and arrive with the demographics work.",
      "County needs a ZIP-to-county table South Carolina does not have here yet. \"My condition isn't listed\" and how often patients ask about insurance both need somewhere to record them — and the insurance count will come from an optional topic a patient picks, never from reading their messages.",
    ],
  },
};

/** Sage, Mint, Sky, Stone: the palette, resolved from the design system. */
const useChartColours = () =>
  useMemo(
    () => ({
      primary: token("--youro-sage"),
      secondary: token("--youro-mint"),
      accent: token("--youro-sky"),
      quiet: token("--youro-stone"),
      text: token("--youro-charcoal"),
      grid: token("--youro-border"),
    }),
    []
  );

const Card = ({ label, value, note }) => (
  <div className="analytics-card">
    <p className="analytics-card-label">{label}</p>
    <p className="analytics-card-value">{value}</p>
    {note ? <p className="analytics-card-note">{note}</p> : null}
  </div>
);

const Section = ({ title, note, children }) => (
  <section className="analytics-section">
    <h3 className="analytics-section-title">{title}</h3>
    {note ? <p className="analytics-note">{note}</p> : null}
    {children}
  </section>
);

/** What a chart shows when the query came back with nothing in it. */
const NoData = ({ children }) => <p className="analytics-empty">{children}</p>;

const Table = ({ columns, rows, empty }) => {
  if (!rows || rows.length === 0) return <NoData>{empty}</NoData>;
  return (
    <div className="analytics-table-wrap">
      <table className="analytics-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.key ?? i}>
              {columns.map((c) => (
                <td key={c.key}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Unbuilt = ({ tab }) => {
  const copy = NOT_YET[tab];
  if (!copy) return null;
  return (
    <section className="analytics-unbuilt">
      <h3 className="analytics-section-title">{copy.heading}</h3>
      {copy.body.map((paragraph) => (
        <p key={paragraph.slice(0, 24)}>{paragraph}</p>
      ))}
    </section>
  );
};

/* --------------------------------------------------------------------- tabs */

const OverviewTab = ({ data, colours }) => {
  if (!data) return null;
  const { patients, members, revenue, visits, outcomes } = data;
  return (
    <>
      <div className="analytics-cards">
        <Card
          label="Patients"
          value={count(patients.total)}
          note={`${count(patients.newInRange)} new this period`}
        />
        <Card
          label="Active memberships"
          value={count(members.active)}
          note={`${count(members.newInRange)} started, ${count(
            members.cancelledInRange
          )} cancelled`}
        />
        <Card
          label="Collected"
          value={money(revenue.collectedCents)}
          note={
            revenue.refundedCents
              ? `${money(revenue.refundedCents)} refunded`
              : "no refunds this period"
          }
        />
        <Card
          label="Annual run rate"
          value={money(revenue.annualisedRunRateCents)}
          note="active memberships at today's prices"
        />
        <Card
          label="Visits completed"
          value={count(visits.completed)}
          note={`${count(visits.upcoming)} upcoming`}
        />
        <Card
          label="Mean symptom improvement"
          value={decimal(outcomes.meanChange, "points")}
          note={
            outcomes.patientsMeasured
              ? `across ${count(outcomes.patientsMeasured)} patients with two scores`
              : "no patient has two scores yet"
          }
        />
      </div>

      {patients.signupDateUnknown > 0 && (
        <p className="analytics-note">
          {count(patients.signupDateUnknown)} accounts have no signup date. They
          were created before that was recorded, so they are counted in the total
          but cannot appear in anything measured over time.
        </p>
      )}

      <Section
        title="Visits this period"
        note="Completed, no-show and cancelled are counted by the date of the visit."
      >
        <div className="analytics-chart">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={[
                { name: "Completed", value: visits.completed },
                { name: "No-show", value: visits.noShow },
                { name: "Cancelled", value: visits.cancelled },
                { name: "Upcoming", value: visits.upcoming },
              ]}
            >
              <CartesianGrid stroke={colours.grid} vertical={false} />
              <XAxis dataKey="name" stroke={colours.text} fontSize={12} />
              <YAxis stroke={colours.text} fontSize={12} allowDecimals={false} />
              <ChartTooltip />
              <Bar isAnimationActive={false} dataKey="value" name="Visits" fill={colours.primary} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>
    </>
  );
};

const MembershipTab = ({ data, colours }) => {
  if (!data) return null;
  const { activeByTier, monthly, annualisedRunRateCents, onDemandToMembership, renewal, refunds, visitUsage } = data;
  const pieColours = [colours.primary, colours.accent, colours.secondary, colours.quiet];

  return (
    <>
      <div className="analytics-cards">
        <Card label="Annual run rate" value={money(annualisedRunRateCents)} />
        <Card
          label="Renewal rate"
          value={percent(renewal.ratePercent)}
          note={
            renewal.ended
              ? `${count(renewal.renewed)} of ${count(renewal.ended)} that ended`
              : "nothing has come up for renewal yet"
          }
        />
        <Card
          label="One-time to membership"
          value={percent(onDemandToMembership.ratePercent)}
          note={
            onDemandToMembership.oneTimeBuyers
              ? `${count(onDemandToMembership.laterSubscribed)} of ${count(
                  onDemandToMembership.oneTimeBuyers
                )} one-time buyers`
              : "no one-time buyers yet"
          }
        />
        <Card
          label="Refunds"
          value={money(refunds.amountCents)}
          note={`${count(refunds.count)} issued`}
        />
      </div>

      <Section title="Active memberships by tier">
        {activeByTier.length === 0 ? (
          <NoData>No active memberships.</NoData>
        ) : (
          <div className="analytics-chart">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={activeByTier}
                  dataKey="activeCount"
                  nameKey="plan"
                  outerRadius={90}
                  isAnimationActive={false}
                  label
                >
                  {activeByTier.map((slice, i) => (
                    <Cell key={slice.plan} fill={pieColours[i % pieColours.length]} />
                  ))}
                </Pie>
                <Legend />
                <ChartTooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      <Section
        title="New and cancelled by month"
        note="Months with no activity are shown as zero rather than skipped, so a flat stretch reads as a flat stretch."
      >
        <div className="analytics-chart">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={monthly}>
              <CartesianGrid stroke={colours.grid} vertical={false} />
              <XAxis dataKey="month" stroke={colours.text} fontSize={12} />
              <YAxis stroke={colours.text} fontSize={12} allowDecimals={false} />
              <ChartTooltip />
              <Legend />
              <Line
                isAnimationActive={false}
                type="monotone"
                dataKey="newCount"
                name="Started"
                stroke={colours.primary}
                strokeWidth={2}
              />
              <Line
                isAnimationActive={false}
                type="monotone"
                dataKey="cancelledCount"
                name="Cancelled"
                stroke={colours.accent}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <Section title="Visits used against visits included">
        <Table
          empty="No subscription has recorded a visit yet."
          rows={visitUsage.map((r) => ({ ...r, key: r.plan }))}
          columns={[
            { key: "plan", label: "Plan", render: (r) => r.plan },
            { key: "subs", label: "Subscriptions", render: (r) => count(r.subscriptions) },
            {
              key: "included",
              label: "Included",
              render: (r) => (r.included === null ? "Unlimited" : count(r.included)),
            },
            { key: "used", label: "Mean used", render: (r) => decimal(r.meanUsed) },
          ]}
        />
      </Section>

      {refunds.byStatus.length > 0 && (
        <Section title="Refunds by status">
          <Table
            empty="No refunds."
            rows={refunds.byStatus.map((r) => ({ ...r, key: r.status }))}
            columns={[
              { key: "status", label: "Status", render: (r) => r.status },
              { key: "count", label: "Count", render: (r) => count(r.count) },
            ]}
          />
        </Section>
      )}
    </>
  );
};

const AccessTab = ({ data, colours }) => {
  if (!data) return null;
  const { bookingToVisit, visitOutcomes, firstResponse, refills } = data;
  return (
    <>
      <div className="analytics-cards">
        <Card
          label="Wait for a visit"
          value={decimal(bookingToVisit.medianDays, "days")}
          note={
            bookingToVisit.measured
              ? `median of ${count(bookingToVisit.measured)}; mean ${decimal(
                  bookingToVisit.meanDays,
                  "days"
                )}`
              : "no booking times recorded yet"
          }
        />
        <Card
          label="No-show rate"
          value={percent(visitOutcomes.noShowRatePercent)}
          note={`${count(visitOutcomes.noShow)} of ${count(
            sum(visitOutcomes.completed, visitOutcomes.noShow)
          )} visits that were due`}
        />
        <Card
          label="Cancellation rate"
          value={percent(visitOutcomes.cancellationRatePercent)}
          note={`${count(visitOutcomes.cancelled)} cancelled`}
        />
        <Card
          label="First reply to a message"
          value={decimal(firstResponse.medianHours, "hours")}
          note={
            firstResponse.answered
              ? `median across ${count(firstResponse.answered)} waits; mean ${decimal(
                  firstResponse.meanHours,
                  "hours"
                )}`
              : "no answered messages this period"
          }
        />
        <Card
          label="Refill turnaround"
          value={decimal(refills.meanHours, "hours")}
          note={`${count(refills.approved)} of ${count(refills.requested)} requests approved`}
        />
      </div>

      {bookingToVisit.unrecorded > 0 && (
        <p className="analytics-note">
          {count(bookingToVisit.unrecorded)} appointments in this period were
          booked before Youro started recording when a booking was made. They are
          left out of the wait times rather than counted as zero.
        </p>
      )}

      {bookingToVisit.implausible > 0 && (
        <p className="analytics-note">
          {count(bookingToVisit.implausible)} appointments record a booking time
          after the appointment itself, which cannot be a wait. They are excluded
          from the averages and worth chasing — a booking timestamp that lands
          after its own visit usually means the row was written by something
          other than the booking flow.
        </p>
      )}

      <Section
        title="What happened to visits"
        note="A no-show is measured against visits that were actually due: a cancelled visit was never going to be attended."
      >
        <div className="analytics-chart">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={[
                { name: "Completed", value: visitOutcomes.completed },
                { name: "No-show", value: visitOutcomes.noShow },
                { name: "Cancelled", value: visitOutcomes.cancelled },
                { name: "Still scheduled", value: visitOutcomes.scheduled },
              ]}
            >
              <CartesianGrid stroke={colours.grid} vertical={false} />
              <XAxis dataKey="name" stroke={colours.text} fontSize={12} />
              <YAxis stroke={colours.text} fontSize={12} allowDecimals={false} />
              <ChartTooltip />
              <Bar isAnimationActive={false} dataKey="value" name="Visits" fill={colours.primary} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>
    </>
  );
};

const OutcomesTab = ({ data, colours }) => {
  if (!data) return null;
  const { byCondition, followUp } = data;
  return (
    <>
      <div className="analytics-cards">
        <Card
          label="Follow-up completion"
          value={percent(followUp.ratePercent)}
          note={
            followUp.remindersSent
              ? `${count(followUp.scoresAfterReminder)} scores after ${count(
                  followUp.remindersSent
                )} reminders`
              : "no follow-up reminders sent this period"
          }
        />
        <Card
          label="Conditions measured"
          value={count(byCondition.length)}
          note="conditions with at least one patient scored twice"
        />
      </div>

      {/*
        The direction matters more than anything else on this tab, and the first
        version of this sentence had it backwards. On Youro's scale a HIGHER
        score means MILDER symptoms — utils/symptomSeverity.js puts 1–29 at
        "Severely Bothersome" and 90–100 at "Mildly Bothersome", and the patient
        chart tells patients "Higher is better". So improvement is latest minus
        baseline, and the server computes it that way.
      */}
      <p className="analytics-note">
        Higher scores mean milder symptoms, so a positive change is an
        improvement. Baseline is each patient's first recorded score for that
        condition; latest is their most recent one in this period. A patient with
        only one score is a baseline, not an outcome, and is not counted here.
      </p>

      <Section title="Symptom change by condition">
        {byCondition.length === 0 ? (
          <NoData>
            No condition has a patient with two scores in this period. Widen the
            date range, or wait for follow-up questionnaires to come in.
          </NoData>
        ) : (
          <div className="analytics-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byCondition} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid stroke={colours.grid} horizontal={false} />
                <XAxis type="number" stroke={colours.text} fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="condition"
                  stroke={colours.text}
                  fontSize={12}
                  width={140}
                />
                <ChartTooltip />
                <Legend />
                <Bar isAnimationActive={false} dataKey="meanBaseline" name="Baseline" fill={colours.quiet} />
                <Bar isAnimationActive={false} dataKey="meanLatest" name="Latest" fill={colours.primary} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      <Section title="Per condition">
        <Table
          empty="Nothing measured in this period."
          rows={byCondition.map((r) => ({ ...r, key: r.condition }))}
          columns={[
            { key: "condition", label: "Condition", render: (r) => r.condition },
            { key: "patients", label: "Patients", render: (r) => count(r.patientsMeasured) },
            { key: "baseline", label: "Baseline", render: (r) => decimal(r.meanBaseline) },
            { key: "latest", label: "Latest", render: (r) => decimal(r.meanLatest) },
            { key: "change", label: "Change", render: (r) => decimal(r.meanChange) },
            {
              key: "split",
              label: "Better / worse / same",
              render: (r) => `${count(r.improved)} / ${count(r.worsened)} / ${count(r.unchanged)}`,
            },
          ]}
        />
      </Section>
    </>
  );
};

/**
 * Keeps one malformed response from blanking the admin console.
 *
 * <p>Every panel reads nested fields off the server's answer — `patients.total`,
 * `refunds.byStatus.length`. A response missing a section throws during render,
 * and an uncaught render error unmounts the whole React tree: not a broken
 * chart, a white page, with the sidebar and the way out gone too. This keeps
 * the failure inside the panel that caused it.
 *
 * <p>Reset by giving it `key={cacheKey}` at the call site, so React discards the
 * instance whenever the tab OR the range changes. An earlier version compared
 * the tab in `componentDidUpdate` instead, which left the message stuck in a
 * case that matters: fail on one range, switch back to a range already loaded,
 * and the tab never changed — so a "did not expect this shape" error sat over
 * correct cached numbers with no way out but a page reload. Keying it is both
 * simpler and the thing that actually covers every transition.
 */
class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <p className="analytics-empty" role="alert">
          These numbers came back in a shape this screen did not expect, so it
          has not shown them rather than showing something wrong. The other tabs
          still work.
        </p>
      );
    }
    return this.props.children;
  }
}

/* ---------------------------------------------------------------- the screen */

const LOADERS = {
  overview: fetchOverview,
  membership: fetchMembership,
  access: fetchAccess,
  outcomes: fetchOutcomes,
};

const AdminAnalytics = () => {
  const [tab, setTab] = useState("overview");
  const [range, setRange] = useState(DEFAULT_RANGE);
  // Keyed by `${tab}:${range}`, so switching back to a tab already loaded does
  // not re-request it and changing the range does.
  //
  // Held in a ref as well as in state, and the ref is what decides whether to
  // fetch. State is committed asynchronously, so a guard that reads it can run
  // before the previous answer has landed and fetch the same thing twice — which
  // is exactly what happened: leaving a range and coming straight back issued a
  // third request for data already in hand. The ref updates the moment the
  // response arrives; the state copy exists only to trigger a render.
  const cacheRef = useRef({});
  const [cache, setCache] = useState({});
  // Both keyed by `${tab}:${range}` rather than held as single flags. With one
  // shared flag, a slow request finishing after the user had already moved on
  // cleared the spinner for a view that was still loading, and an error from
  // the range they left stayed on screen above correct numbers for the range
  // they returned to — a red "could not load" over numbers that had loaded
  // perfectly well. Keying both means a message can only ever appear on the
  // view it belongs to.
  const [loadingKey, setLoadingKey] = useState("");
  const [errors, setErrors] = useState({});
  const colours = useChartColours();

  const cacheKey = `${tab}:${range}`;
  const data = cache[cacheKey];
  const error = errors[cacheKey] || "";
  const loading = loadingKey === cacheKey;

  const load = useCallback(
    async ({ force = false } = {}) => {
      const loader = LOADERS[tab];
      if (!loader) return;
      const key = `${tab}:${range}`;
      if (!force && cacheRef.current[key]) return;
      setLoadingKey(key);
      setErrors((previous) => ({ ...previous, [key]: "" }));
      try {
        const result = await loader(range);
        cacheRef.current = { ...cacheRef.current, [key]: result };
        setCache(cacheRef.current);
      } catch (err) {
        setErrors((previous) => ({
          ...previous,
          [key]: errorMessage(err, "Could not load these numbers."),
        }));
      } finally {
        // Only if nothing newer has started, or this would clear the spinner
        // for a request still in flight.
        setLoadingKey((current) => (current === key ? "" : current));
      }
    },
    [tab, range]
  );

  useEffect(() => {
    load();
  }, [load]);

  // The dates the server resolved, where there are any. On a tab with no
  // endpoint behind it there is no server answer to quote, so the caption
  // falls back to the button label rather than showing a dash next to a range
  // filter that is plainly set.
  const caption = data?.range
    ? rangeCaption(data.range)
    : RANGES.find((option) => option.key === range)?.label ?? EMPTY;

  return (
    <div className="hm admin-analytics-page">
      {/* Same shell as every other admin screen: the sidebar in .sidebar, the
          content in .admin-ui-table, so Admin-Mobile.css's phone rules apply
          here too without a second set of them. */}
      <div className="sidebar">
        <AdminSideBar data={"admin-analytics"} />
      </div>
      <div className="admin-ui-table analytics-body">
        <div className="header" style={{ marginLeft: "15px" }}>
          <Youroheader />
        </div>
        <header className="analytics-header">
          <div>
            <h2 className="analytics-title">Analytics</h2>
            <p className="analytics-caption">{caption}</p>
          </div>
          <div
            className="analytics-ranges"
            role="group"
            aria-label="Date range"
          >
            {RANGES.map((option) => (
              <button
                key={option.key}
                type="button"
                className={
                  option.key === range
                    ? "analytics-range analytics-range-on"
                    : "analytics-range"
                }
                aria-pressed={option.key === range}
                onClick={() => setRange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </header>

        {/* A real tablist rather than a nav of links. These buttons swap a panel
            on the page; they do not navigate, so aria-current="page" would have
            a screen reader announce "current page" for something that never
            changes page. */}
        <div className="analytics-tabs" role="tablist" aria-label="Analytics sections">
          {TABS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              id={`analytics-tab-${option.key}`}
              aria-controls="analytics-panel"
              aria-selected={option.key === tab}
              className={
                option.key === tab ? "analytics-tab analytics-tab-on" : "analytics-tab"
              }
              onClick={() => setTab(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div
          id="analytics-panel"
          role="tabpanel"
          aria-labelledby={`analytics-tab-${tab}`}
        >
          {error ? (
            <div className="analytics-error" role="alert">
              <p>{error}</p>
              <button
                type="button"
                className="analytics-retry"
                disabled={loading}
                onClick={() => load({ force: true })}
              >
                {loading ? "Trying…" : "Try again"}
              </button>
            </div>
          ) : null}

          {/* role="status" so a screen reader is told the numbers are on their
              way, rather than being left on a silent empty panel. */}
          {loading && !data ? (
            <p className="analytics-loading" role="status">
              Loading…
            </p>
          ) : null}

          {!LOADERS[tab] ? <Unbuilt tab={tab} /> : null}

          {/* Each panel is wrapped, so a response missing a section shows this
              screen's own error rather than throwing out of render and taking
              the whole admin console down with a blank page. */}
          {data ? (
            <TabErrorBoundary key={cacheKey}>
              {tab === "overview" ? <OverviewTab data={data} colours={colours} /> : null}
              {tab === "membership" ? <MembershipTab data={data} colours={colours} /> : null}
              {tab === "access" ? <AccessTab data={data} colours={colours} /> : null}
              {tab === "outcomes" ? <OutcomesTab data={data} colours={colours} /> : null}
            </TabErrorBoundary>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
